/**
 * The text the coach's part of the exercise settings page shows (#6428): the name of each
 * intensity day, and the lines of the Changes list. Kept apart from the page so the wording rules
 * can be unit-tested without Angular. See `design-docs/coach-exercise-editing.md`.
 */

import { DateTime } from 'luxon';
import { IntensityLevel } from '@cf-shared/data-model/fitness-plan';
import { ExerciseChangeDoc, ExerciseChangeEntry, ExerciseChangeField } from '@cf-shared/data-model/exercise-change';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { PostSetStatus, remapOldPostSetStatusNumberToEnum } from '@cf-shared/enums';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { IntensityScheme } from './scheme-switch';

/** the most lines the Changes list shows */
export const CHANGES_SHOWN = 10;

/** the name of one intensity day ("Heavy Day"), or "Every Session" for a one-target exercise */
export function dayLabel(level: IntensityLevel | undefined): TranslatedString {
  switch (level) {
    case IntensityLevel.DUPheavy:
    case IntensityLevel.TUPheavy:
      return LL.exerciseConfiguration.coach.dayHeavy();
    case IntensityLevel.TUPmedium:
      return LL.exerciseConfiguration.coach.dayMedium();
    case IntensityLevel.DUPlight:
    case IntensityLevel.TUPlight:
      return LL.exerciseConfiguration.coach.dayLight();
    default:
      return LL.exerciseConfiguration.coach.dayOne();
  }
}

/** the label of a scheme on the segment: One Target, DUP, TUP */
export function schemeLabel(scheme: IntensityScheme | string | undefined): TranslatedString {
  switch (scheme) {
    case IntensityScheme.Dup: return LL.common.programTypes.dup();
    case IntensityScheme.Tup: return LL.common.programTypes.tup();
    default: return LL.exerciseConfiguration.coach.oneTarget();
  }
}

const shown = (value: string | number | boolean | undefined) =>
  value === undefined || value === null || value === '' ? LL.exerciseConfiguration.coach.field.none() : String(value);

/**
 * One change as a line of plain text. `formatWeight` turns a kg value into the reader's unit; the
 * other numbers are reps and sets, shown as they are.
 */
export function describeChangeEntry(entry: ExerciseChangeEntry, formatWeight: (kg: number) => string): TranslatedString {
  const field = LL.exerciseConfiguration.coach.field;
  const day = dayLabel(entry.level);
  const weight = (value: unknown) => (typeof value === 'number' ? formatWeight(value) : shown(value as undefined));
  switch (entry.field) {
    case ExerciseChangeField.Scheme:
      return field.scheme({ before: schemeLabel(entry.before as string), after: schemeLabel(entry.after as string) });
    case ExerciseChangeField.TargetReps:
      return field.targetReps({ day, before: shown(entry.before), after: shown(entry.after) });
    case ExerciseChangeField.TargetWeight:
      return field.targetWeight({ day, before: weight(entry.before), after: weight(entry.after) });
    case ExerciseChangeField.MinReps:
      return field.minReps({ day, before: shown(entry.before), after: shown(entry.after) });
    case ExerciseChangeField.Volume:
      return field.volume({ before: shown(entry.before), after: shown(entry.after) });
    case ExerciseChangeField.Myo:
      return field.myo();
    case ExerciseChangeField.RmTesting:
      return field.rmTesting();
    case ExerciseChangeField.SetAsideCleared:
      return field.setAsideCleared();
    case ExerciseChangeField.PlateauAnswered:
      return field.plateauAnswered();
    case ExerciseChangeField.VolumeQuestionAnswered:
      return field.volumeQuestionAnswered();
    default:
      // a field written by a newer app: say nothing rather than something wrong
      return field.none();
  }
}

/** one change document as one line: every change it lists, joined */
export function describeChangeDoc(doc: ExerciseChangeDoc, formatWeight: (kg: number) => string): TranslatedString {
  return (doc.changes ?? []).map(entry => describeChangeEntry(entry, formatWeight)).join('; ') as TranslatedString;
}

/** one line of the Changes list */
export interface ChangeRow {
  time: DateTime;
  who: string;
  what: TranslatedString;
}

const APP_EVENT_STATUSES: ReadonlyMap<PostSetStatus, () => TranslatedString> = new Map([
  [PostSetStatus.PlateauDetected, () => LL.exerciseConfiguration.coach.event.plateau()],
  [PostSetStatus.ProgressionDeload, () => LL.exerciseConfiguration.coach.event.deload()],
  [PostSetStatus.ProgressionDeloadLastSet, () => LL.exerciseConfiguration.coach.event.deload()],
  [PostSetStatus.Weakness, () => LL.exerciseConfiguration.coach.event.weakness()],
  [PostSetStatus.VolumeAutoregulationIncrease, () => LL.exerciseConfiguration.coach.event.volumeUp()],
  [PostSetStatus.VolumeAutoregulationDecrease, () => LL.exerciseConfiguration.coach.event.volumeDown()],
]);

/** the app's own events for one exercise in one workout, each kind once, in the order first seen */
export function appEventsOfWorkout(workout: Log_Workout, exerciseId: string): PostSetStatus[] {
  const seen: PostSetStatus[] = [];
  const sets = (workout?.exercises ?? []).filter(entry => entry?.exerciseId === exerciseId).flatMap(entry => entry.sets ?? []);
  for (const status of sets.flatMap(set => set?.postStatus ?? [])) {
    // a status written by an old app is a number
    const code = typeof status?.status === 'number' ? remapOldPostSetStatusNumberToEnum[status.status] : status?.status;
    if (APP_EVENT_STATUSES.has(code) && !seen.includes(code)) {
      seen.push(code);
    }
  }
  return seen;
}

/**
 * The Changes list: the coach's saved changes merged with the app's own events from the recent
 * workouts (plateaus, deloads, weakness results, volume changes), newest first, at most
 * `CHANGES_SHOWN` lines. An event is listed once per workout even when several sets carry it.
 */
export function buildChangeRows(
  docs: readonly ExerciseChangeDoc[],
  workouts: readonly Log_Workout[],
  exerciseId: string,
  formatWeight: (kg: number) => string,
): ChangeRow[] {
  const rows: ChangeRow[] = [];
  for (const doc of docs) {
    const time = DateTime.fromISO(doc.changed_utc ?? '');
    if (!time.isValid) continue;
    rows.push({ time, who: doc.author?.name || LL.exerciseConfiguration.coach.app(), what: describeChangeDoc(doc, formatWeight) });
  }
  for (const workout of workouts) {
    const time = DateTime.fromISO(workout?.time_utc ?? '');
    if (!time.isValid) continue;
    for (const status of appEventsOfWorkout(workout, exerciseId)) {
      rows.push({ time, who: LL.exerciseConfiguration.coach.app(), what: APP_EVENT_STATUSES.get(status)() });
    }
  }
  return rows.sort((a, b) => b.time.toMillis() - a.time.toMillis()).slice(0, CHANGES_SHOWN);
}
